import React from 'react'
import { t } from 'ttag'
import YumTeamSelect from '../YumTeamSelect/YumTeamSelect'
import { CompanyId, DivisionDto, DivisionId, DivisionRow, UserId } from '../../services/api.types'
import CreateTeamWrapper from '../../views/TeamManagement/components/CreateTeam/CreateTeamWrapper'
import DeleteTeamWrapper from '../../views/TeamManagement/components/DeleteTeam/DeleteTeamWrapper'
import { isCompanyDivisionRequired, isCompanyUserCreatedTeams } from '../../views/RegisterUser/RegisterUser'
import ErrorMessage from '../ErrorMessage/ErrorMessage'

interface TeamSelectionUIProps {
  companyId: CompanyId
  userId: UserId
  divisionId: DivisionId | null
  divisions: DivisionDto[]
  teamSelectionLocked?: boolean
  teamSwitchingDisabled?: boolean
  maxTeamSize?: number
  useAutocompleteSelect?: boolean
  onDivisionChange: (companyId: CompanyId, userId: UserId) => (event: React.ChangeEvent<{ value: unknown }>) => void
  showIcon?: boolean
  isDuringRegistration?: boolean
  error?: boolean
  errorMessage?: string
  disabled?: boolean
}

/**
 * TeamSelectionUI component
 *
 * A reusable component for team selection interface that can be used across different views
 * including Settings, RegisterUser, and JoinAnotherCompany.
 *
 * Features:
 * - Shows team selection dropdown with available teams for a company
 * - Allows creating a new team if user-created teams are enabled
 * - Allows deleting a team created by the user
 * - Handles team selection restrictions based on company settings
 * - Shows appropriate error messages for validation errors
 */
const TeamSelectionUI: React.FC<TeamSelectionUIProps> = ({
  companyId,
  userId,
  divisionId,
  divisions,
  teamSelectionLocked = false,
  teamSwitchingDisabled = false,
  maxTeamSize,
  useAutocompleteSelect = false,
  onDivisionChange,
  showIcon = false,
  isDuringRegistration = false,
  error = false,
  errorMessage: errorMessageProp,
  disabled = false,
}) => {
  const isDivisionRequired = isCompanyDivisionRequired(companyId)
  const companyDivisions = divisions.filter((d) => d.companyId === companyId)
  const divisionLocked = !divisionId ? false : companyDivisions.find((d) => d.id === divisionId)?.locked || false

  const createdDivisionId = companyDivisions.find((d) => d.creatorUserId === userId)?.id
  const createdDivisionMembers = companyDivisions.find((d) => d.creatorUserId === userId)?.members

  // User can create teams if:
  // 1. Company has user-created teams enabled AND
  // 2. Either user has no division OR team switching is not disabled OR
  // 3. User's current team is not locked
  const isUserCreatedTeams =
    isCompanyUserCreatedTeams(companyId) && (!teamSwitchingDisabled || !divisionId) && !divisionLocked

  if (!companyDivisions.length && !isUserCreatedTeams) {
    return null
  }

  // Default error message for team selection
  const errorMessage = errorMessageProp || t`Team selection is required.`

  const isDisabled =
    (teamSwitchingDisabled && !!divisionId) ||
    teamSelectionLocked ||
    divisionLocked ||
    (!!createdDivisionId && !!divisionId && createdDivisionId === divisionId) ||
    // Optionally disabled while loading by JoinAnotherCompany component
    disabled

  return (
    <div>
      {/* <div>divisionId: {divisionId}</div>
      <div>createdDivisionId: {createdDivisionId}</div> */}
      {companyDivisions.length > 0 && (
        <div
          style={{
            marginBottom: isUserCreatedTeams || !isDuringRegistration ? 0 : '32px',
          }}
        >
          <YumTeamSelect
            disabled={isDisabled}
            label={t`Team`}
            companyDivisions={companyDivisions}
            value={divisionId || ''}
            required={isDivisionRequired}
            onChange={onDivisionChange(companyId, userId)}
            error={error}
            showIcon={showIcon}
            useAutocompleteSelect={useAutocompleteSelect}
            maxTeamSize={maxTeamSize}
            style={{
              marginBottom: 0,
            }}
            isDuringRegistration={isDuringRegistration}
          />
          {error && <ErrorMessage>{errorMessage}</ErrorMessage>}
          {(teamSwitchingDisabled && !!divisionId) || teamSelectionLocked || divisionLocked ? (
            <div
              style={{
                fontSize: '12px',
                opacity: 0.5,
                marginTop: 3,
              }}
            >
              {teamSwitchingDisabled && !!divisionId
                ? t`Team switching is disabled`
                : teamSelectionLocked
                  ? t`Team selection is locked`
                  : divisionLocked
                    ? t`This team is locked`
                    : null}
            </div>
          ) : null}
        </div>
      )}
      {isUserCreatedTeams && (
        <div
          style={{
            marginBottom: !isDuringRegistration ? 0 : '38px',
          }}
        >
          {!createdDivisionId ? (
            <CreateTeamWrapper
              variant="link"
              text={t`Create new team`}
              companyId={companyId}
              isUserCreated={true}
              isDuringRegistration={true}
              style={{
                marginBottom: '0px',
              }}
            />
          ) : isDuringRegistration || !isDivisionRequired || !createdDivisionMembers || createdDivisionMembers <= 1 ? (
            <DeleteTeamWrapper
              text={t`Delete team`}
              divisionId={createdDivisionId}
              companyId={companyId}
              members={createdDivisionMembers}
              isDuringRegistration={true}
              style={{
                marginBottom: '0px',
              }}
            />
          ) : (
            <div
              style={{
                fontSize: '12px',
                opacity: 0.5,
                marginTop: 3,
              }}
            >
              {t`Teams with members can’t be deleted`}
            </div>
          )}
          {companyDivisions.length === 0 && error ? <ErrorMessage>{errorMessage}</ErrorMessage> : null}
        </div>
      )}
    </div>
  )
}

export default TeamSelectionUI
